// SettlementDialog.tsx — "Post settlement" (wireframe S7, Insurance Claims
// Phase 4.5.6): received-on/UTR/gross/TDS/net fields, a deductions table
// with a recover/write-off/appeal action per row, the reconciliation check
// line, and a "what will be posted" per-invoice preview — all computed from
// the SAME pure functions (`utils/claimSettlement.js`'s validateSettlement/
// allocateSettlement, via POST /:id/settlements) the server will actually
// run; nothing here is a separate re-derivation of the money math, only the
// invoice list (`GET /:id/invoices`) and the mutation itself.
import { useMemo, useState } from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/Dialog';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { SegmentedControl } from '@/components/ui/SegmentedControl';
import { Badge } from '@/components/ui/Badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/Table';
import { LoadingState, ErrorState } from '@/components/ui/States';
import { ApiError } from '@/lib/api/client';
import { DocumentUploadField } from './DocumentUploadField';
import { useClaimInvoices, usePostSettlement } from './hooks';
import type { ClaimWorkspace, DeductionAction, SettlementDeductionInput } from './types';

const round2 = (v: number) => Math.round((v + Number.EPSILON) * 100) / 100;

interface DeductionRow extends SettlementDeductionInput { _key: string }

const ACTION_OPTIONS: Array<{ value: DeductionAction; label: string }> = [
  { value: 'recover', label: 'Recover from patient' },
  { value: 'write_off', label: 'Write off' },
  { value: 'appeal', label: 'Appeal' },
];

function money(v: number): string {
  return `₹${v.toFixed(2)}`;
}

/** Mirrors utils/claimSettlement.js#allocateSettlement — client preview
 * only, so the dialog's "what will be posted" table reflects an edit the
 * instant it's typed, without a round trip; the server re-runs the real
 * function and is authoritative. */
function previewAllocation(
  invoices: { billId: number | string; billType: string; payerDue: number }[],
  netReceived: number, tds: number, deductions: SettlementDeductionInput[]
) {
  const order = ['PH', 'IPPH', 'IP'];
  const working = invoices.map((i) => ({ ...i, recover: 0, writeOff: 0, appeal: 0 }));
  const byId = new Map(working.map((i) => [String(i.billId), i]));
  const byType = new Map<string, typeof working>();
  for (const i of working) { if (!byType.has(i.billType)) byType.set(i.billType, []); byType.get(i.billType)!.push(i); }
  const unlisted = [...byType.keys()].filter((t) => !order.includes(t));
  const typeOrder = [...order, ...unlisted];
  const capacity = (i: typeof working[number]) => round2(i.payerDue - i.recover - i.writeOff);
  // DeductionAction values are snake_case ('write_off'); the preview's own
  // accumulator fields are camelCase (matching allocateSettlement()'s own
  // `deductionByAction`/`deductionsByBill` shape) — this is the one place
  // that translates between the two, rather than indexing by the action
  // string directly (which would silently no-op on 'write_off').
  const bump = (i: typeof working[number], action: SettlementDeductionInput['action'], amount: number) => {
    if (action === 'recover') i.recover = round2(i.recover + amount);
    else if (action === 'write_off') i.writeOff = round2(i.writeOff + amount);
    else if (action === 'appeal') i.appeal = round2(i.appeal + amount);
  };

  for (const d of deductions) {
    let remaining = round2(Number(d.amount) || 0);
    const target = d.bill_id != null ? byId.get(String(d.bill_id)) : null;
    if (target) {
      const take = round2(Math.min(capacity(target), remaining));
      if (take > 0) { bump(target, d.action, take); remaining = round2(remaining - take); }
    }
    if (remaining <= 0) continue;
    for (const t of typeOrder) {
      if (remaining <= 0) break;
      for (const i of byType.get(t) ?? []) {
        if (remaining <= 0) break;
        const cap = capacity(i);
        if (cap <= 0) continue;
        const take = round2(Math.min(cap, remaining));
        bump(i, d.action, take);
        remaining = round2(remaining - take);
      }
    }
  }

  let grossRemaining = round2(netReceived + tds);
  const grossTotalRequested = grossRemaining;
  const tdsTotal = round2(tds);
  const raw: { billId: number | string; grossAmount: number }[] = [];
  for (const i of working) {
    if (grossRemaining <= 0) break;
    const cap = capacity(i);
    if (cap <= 0) continue;
    const gross = round2(Math.min(cap, grossRemaining));
    raw.push({ billId: i.billId, grossAmount: gross });
    grossRemaining = round2(grossRemaining - gross);
  }
  let tdsRemaining = tdsTotal;
  const allocations = raw.map((a, idx) => {
    const isLast = idx === raw.length - 1;
    const tdsPortion = isLast ? tdsRemaining : round2(grossTotalRequested > 0 ? (a.grossAmount / grossTotalRequested) * tdsTotal : 0);
    tdsRemaining = round2(tdsRemaining - tdsPortion);
    return { billId: a.billId, grossAmount: a.grossAmount, tdsAmount: round2(Math.max(0, tdsPortion)), netAmount: round2(a.grossAmount - Math.max(0, tdsPortion)) };
  });
  return { allocations, working, unallocatedGross: grossRemaining };
}

export interface SettlementDialogProps {
  claim: ClaimWorkspace;
  open: boolean;
  onClose: () => void;
}

export function SettlementDialog({ claim, open, onClose }: SettlementDialogProps) {
  const invoicesQuery = useClaimInvoices(open ? claim.id : null);
  const postMutation = usePostSettlement(claim.id);

  const [receivedOn, setReceivedOn] = useState(() => new Date().toISOString().slice(0, 10));
  const [utr, setUtr] = useState('');
  const [tds, setTds] = useState('0');
  const [netReceived, setNetReceived] = useState('');
  const [adviceDocumentId, setAdviceDocumentId] = useState<number | null>(null);
  const [adviceDocumentName, setAdviceDocumentName] = useState<string | null>(null);
  const [deductions, setDeductions] = useState<DeductionRow[]>([]);
  const [error, setError] = useState('');

  const invoices = useMemo(() => invoicesQuery.data ?? [], [invoicesQuery.data]);
  const totalPayerDue = round2(invoices.reduce((s, i) => s + i.payerDue, 0));
  const tdsNum = Number(tds) || 0;
  const netNum = Number(netReceived) || 0;
  const deductionTotal = round2(deductions.reduce((s, d) => s + (Number(d.amount) || 0), 0));
  const grossApproved = round2(netNum + tdsNum + deductionTotal);
  const diff = round2(grossApproved - totalPayerDue);
  const reconciles = Math.abs(diff) <= 1;

  const addDeduction = () => setDeductions((d) => [...d, { _key: String(Date.now() + Math.random()), amount: 0, action: 'recover' }]);
  const updateDeduction = (key: string, patch: Partial<DeductionRow>) =>
    setDeductions((d) => d.map((row) => (row._key === key ? { ...row, ...patch } : row)));
  const removeDeduction = (key: string) => setDeductions((d) => d.filter((row) => row._key !== key));

  const preview = useMemo(
    () => previewAllocation(invoices, netNum, tdsNum, deductions.filter((d) => Number(d.amount) > 0)),
    [invoices, netNum, tdsNum, deductions]
  );

  // The status the server will move the claim to (claimSettlement.js#
  // postSettlement): an appeal wins where allowed, any deduction or any
  // payer due left unsettled is short-settled, otherwise settled.
  const predictedStatus = deductions.some((d) => d.action === 'appeal' && Number(d.amount) > 0)
    ? 'Appealed'
    : (deductionTotal > 0 || preview.unallocatedGross > 0 || round2(netNum + tdsNum + deductionTotal) < totalPayerDue - 1) ? 'Short-settled' : 'Settled';
  const canSave = receivedOn && netNum >= 0 && tdsNum >= 0 && deductions.every((d) => d.action && Number(d.amount) > 0);

  const save = async () => {
    setError('');
    try {
      await postMutation.mutateAsync({
        received_on: receivedOn, utr: utr.trim() || undefined, gross_approved: grossApproved,
        tds: tdsNum, net_received: netNum, advice_document_id: adviceDocumentId ?? undefined,
        deductions: deductions.filter((d) => Number(d.amount) > 0).map(({ _key: _k, ...d }) => d),
      });
      onClose();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Post settlement — {claim.claim_ref}</DialogTitle>
        </DialogHeader>

        {invoicesQuery.isLoading && <LoadingState label="Loading invoices…" />}
        {invoicesQuery.isError && (
          <ErrorState message={invoicesQuery.error instanceof ApiError ? invoicesQuery.error.message : 'Could not load invoices'} onRetry={() => invoicesQuery.refetch()} />
        )}
        {!invoicesQuery.isLoading && !invoicesQuery.isError && (
          <div className="flex flex-col gap-4">
            {invoices.length === 0 && (
              <div className="rounded-[var(--radius)] bg-warn-soft px-3 py-2 text-[12px] text-warn-ink">
                This claim has no allocated invoices to settle yet.
              </div>
            )}
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <FormField label="Received on" required>
                <Input type="date" value={receivedOn} onChange={(e) => setReceivedOn(e.target.value)} />
              </FormField>
              <FormField label="UTR / NEFT ref">
                <Input value={utr} onChange={(e) => setUtr(e.target.value)} />
              </FormField>
              <FormField label="Gross approved ₹">
                <Input value={money(grossApproved)} disabled title="Net received + TDS + deductions" />
              </FormField>
              <FormField label="TDS ₹">
                <Input type="number" min={0} value={tds} onChange={(e) => setTds(e.target.value)} />
              </FormField>
              <FormField label="Net received ₹" required>
                <Input type="number" min={0} value={netReceived} onChange={(e) => setNetReceived(e.target.value)} />
              </FormField>
              <FormField label="Payer due on invoices">
                <Input value={money(totalPayerDue)} disabled />
              </FormField>
              <FormField label="Settlement advice">
                <DocumentUploadField
                  uhid={claim.uhid ?? ''} category="Other" uploadedFileName={adviceDocumentName}
                  onUploaded={(id) => { setAdviceDocumentId(id); setAdviceDocumentName('Uploaded'); }}
                />
              </FormField>
            </div>

            <div>
              <div className="mb-2 flex items-center justify-between">
                <div className="text-[12px] font-semibold uppercase tracking-wide text-ink-muted">Deductions at settlement</div>
                <Button size="sm" variant="ghost" onClick={addDeduction}>+ Add deduction</Button>
              </div>
              {deductions.length > 0 && (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Head</TableHead><TableHead>Reason</TableHead>
                      <TableHead className="text-right">Amount ₹</TableHead><TableHead>Action</TableHead><TableHead />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {deductions.map((d) => (
                      <TableRow key={d._key}>
                        <TableCell><Input value={d.head ?? ''} onChange={(e) => updateDeduction(d._key, { head: e.target.value })} className="h-7 text-[12px]" /></TableCell>
                        <TableCell><Input value={d.description ?? ''} onChange={(e) => updateDeduction(d._key, { description: e.target.value })} className="h-7 text-[12px]" /></TableCell>
                        <TableCell className="text-right"><Input type="number" min={0} value={d.amount || ''} onChange={(e) => updateDeduction(d._key, { amount: Number(e.target.value) })} className="h-7 w-24 text-right text-[12px]" /></TableCell>
                        <TableCell>
                          <SegmentedControl size="sm" options={ACTION_OPTIONS} value={d.action} onChange={(v) => updateDeduction(d._key, { action: v })} />
                        </TableCell>
                        <TableCell><Button size="sm" variant="ghost" onClick={() => removeDeduction(d._key)}>✕</Button></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </div>

            <div className="rounded-[var(--radius)] bg-muted px-3 py-2 text-[11.5px]">
              Check: net {money(netNum)} + TDS {money(tdsNum)} + deductions {money(deductionTotal)} = {money(grossApproved)}
              {reconciles ? (
                <Badge variant="success" className="ml-2">✓ matches payer due {money(totalPayerDue)}</Badge>
              ) : (
                <Badge variant="danger" className="ml-2">Off by {money(diff)} — server will reject (422)</Badge>
              )}
            </div>

            {preview.allocations.length > 0 && (
              <div>
                <div className="mb-2 text-[12px] font-semibold uppercase tracking-wide text-ink-muted">What will be posted</div>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead><TableHead className="text-right">Payer due</TableHead>
                      <TableHead className="text-right">Insurer payment (incl. TDS)</TableHead><TableHead className="text-right">→ patient</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {preview.allocations.map((a) => {
                      const inv = preview.working.find((w) => String(w.billId) === String(a.billId));
                      const toPatient = round2((inv?.recover ?? 0));
                      return (
                        <TableRow key={String(a.billId)}>
                          <TableCell>Bill #{a.billId}</TableCell>
                          <TableCell className="text-right">{money(inv?.payerDue ?? 0)}</TableCell>
                          <TableCell className="text-right">{money(a.grossAmount)}</TableCell>
                          <TableCell className="text-right">{money(toPatient)}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {error && <div className="text-[12px] text-danger-ink">{error}</div>}

            <div className="flex justify-end gap-2">
              <DialogPrimitive.Close asChild><Button variant="ghost">Cancel</Button></DialogPrimitive.Close>
              <Button loading={postMutation.isPending} disabled={!canSave} onClick={save}>Post settlement → status: {predictedStatus}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
